import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { CurrentUserProvider } from '@/hooks/useCurrentUser';

interface CostCenter {
    id: string;
    name: string;
}

interface CompanyOption { id: string; name: string }

interface PaymentInstrument {
    id: string;
    name: string;
    instrument_type: string;
    account_number?: string;
    iban?: string;
    bank_name?: string;
    cost_center_id?: string;
    company_id?: string;
    is_active: boolean;
    created_at: string;
    updated_at?: string;
    cost_center?: {
        id: string;
        name: string;
    };
    company?: { id: string; name: string };
}

interface PaymentInstrumentFormData {
    name: string;
    instrument_type: string;
    account_number: string;
    iban: string;
    bank_name: string;
    cost_center_id: string;
    company_id: string;
    is_active: boolean;
}

export default function EditPaymentInstrumentPage() {
    const navigate = useNavigate();
    const params = useParams({ strict: false });
    const routerState = useRouterState();
    const { id } = params;
    const { t, isReady } = useI18n();
    const [paymentInstrument, setPaymentInstrument] = useState<PaymentInstrument | null>(null);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [companies, setCompanies] = useState<CompanyOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [formData, setFormData] = useState<PaymentInstrumentFormData>({
        name: '',
        instrument_type: '',
        account_number: '',
        iban: '',
        bank_name: '',
        cost_center_id: '',
        company_id: '',
        is_active: true,
    });

    useEffect(() => {
        if (id && isReady) {
            fetchPaymentInstrument();
            fetchReferenceData();
        }
    }, [id, isReady]);

    const fetchPaymentInstrument = async () => {
        try {
            setLoading(true);
            const response = await apiFetch(`/api/payment-instruments/${id}`);
            if (response.ok) {
                const data = await response.json();
                setPaymentInstrument(data);
                setFormData({
                    name: data.name || '',
                    instrument_type: data.instrument_type || '',
                    account_number: data.account_number || '',
                    iban: data.iban || '',
                    bank_name: data.bank_name || '',
                    cost_center_id: data.cost_center_id || '',
                    company_id: data.company_id || data.company?.id || '',
                    is_active: data.is_active ?? true,
                });
            } else {
                console.error('Failed to load payment instrument');
                navigate({ to: '/admin/finance' });
            }
        } catch (error) {
            console.error('Error fetching payment instrument:', error);
            navigate({ to: '/admin/finance' });
        } finally {
            setLoading(false);
        }
    };

    const fetchReferenceData = async () => {
        try {
            const [ccResp, compResp] = await Promise.all([
                apiFetch('/api/cost-centers/'),
                apiFetch('/api/companies/')
            ]);
            if (ccResp.ok) {
                const data = await ccResp.json();
                setCostCenters(Array.isArray(data) ? data : []);
            }
            if (compResp.ok) {
                const cdata = await compResp.json();
                setCompanies(Array.isArray(cdata) ? cdata : []);
            }
        } catch (error) {
            console.error('Error fetching reference data:', error);
        }
    };

    const handleInputChange = (field: keyof PaymentInstrumentFormData, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = t('forms.required', 'common');
        }
        if (!formData.instrument_type) {
            newErrors.instrument_type = t('forms.required', 'common');
        }
        if (!formData.company_id) {
            newErrors.company_id = t('forms.required', 'common');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSaving(true);

        try {
            const submitData = {
                name: formData.name.trim(),
                instrument_type: formData.instrument_type,
                account_number: formData.account_number.trim() || undefined,
                iban: formData.iban.trim() || undefined,
                bank_name: formData.bank_name.trim() || undefined,
                cost_center_id: formData.cost_center_id || undefined,
                company_id: formData.company_id || undefined,
                is_active: formData.is_active,
            };

            const response = await apiFetch(`/api/payment-instruments/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submitData),
            });

            if (response.ok) {
                navigate({ to: '/admin/finance' });
            } else {
                const errorData = await response.json();
                console.error('API response not ok:', response.status, response.statusText, errorData);
                alert(t('finance.paymentInstruments.updateFailed', 'admin'));
            }
        } catch (error) {
            console.error('Error updating payment instrument:', error);
            alert(t('finance.paymentInstruments.updateFailed', 'admin'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!paymentInstrument || !confirm(t('finance.paymentInstruments.deleteConfirm', 'admin'))) {
            return;
        }

        try {
            const response = await apiFetch(`/api/payment-instruments/${paymentInstrument.id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                navigate({ to: '/admin/finance' });
            } else {
                alert(t('finance.paymentInstruments.deleteFailed', 'admin'));
            }
        } catch (error) {
            console.error('Error deleting payment instrument:', error);
            alert(t('finance.paymentInstruments.deleteFailed', 'admin'));
        }
    };

    const getInstrumentTypeName = (type: string) => {
        switch (type.toLowerCase()) {
            case 'bank_account':
                return t('finance.paymentInstruments.types.bank_account', 'admin');
            case 'credit_card':
                return t('finance.paymentInstruments.types.credit_card', 'admin');
            case 'cash':
                return t('finance.paymentInstruments.types.cash', 'admin');
            default:
                return type;
        }
    };

    if (!isReady || loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg">{t('actions.loading', 'common')}</div>
                </div>
            </Layout>
        );
    }

    if (!paymentInstrument) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg">{t('finance.paymentInstruments.notFound', 'admin')}</div>
                </div>
            </Layout>
        );
    }

    return (
        <CurrentUserProvider>
            <Layout>
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="outline"
                                onClick={() => navigate({ to: '/admin/finance' })}
                                className="flex items-center gap-2"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                {t('finance.backToFinance', 'admin')}
                            </Button>
                            <div>
                                <h1 className="text-3xl font-bold tracking-tight">
                                    {t('finance.paymentInstruments.editTitle', 'admin')}
                                </h1>
                                <p className="text-gray-600">
                                    {paymentInstrument.name} • {getInstrumentTypeName(paymentInstrument.instrument_type)}
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            className="flex items-center gap-2"
                        >
                            <Trash2 className="h-4 w-4" />
                            {t('actions.delete', 'common')}
                        </Button>
                    </div>

                    <Card className="max-w-2xl">
                        <CardHeader>
                            <CardTitle>{t('finance.paymentInstruments.detailsTitle', 'admin')}</CardTitle>
                            <CardDescription>
                                {t('finance.paymentInstruments.detailsDescription', 'admin')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="name">{t('finance.paymentInstruments.fields.name', 'admin')}</Label>
                                        <Input
                                            id="name"
                                            value={formData.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            placeholder={t('finance.paymentInstruments.placeholders.name', 'admin')}
                                            className={errors.name ? 'border-red-500' : ''}
                                        />
                                        {errors.name && (
                                            <p className="text-sm text-red-600">{errors.name}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="instrument_type">{t('finance.paymentInstruments.fields.type', 'admin')}</Label>
                                        <Select
                                            value={formData.instrument_type}
                                            onValueChange={(value) => handleInputChange('instrument_type', value)}
                                        >
                                            <SelectTrigger className={errors.instrument_type ? 'border-red-500' : ''}>
                                                <SelectValue placeholder={t('finance.paymentInstruments.placeholders.type', 'admin')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="bank_account">{t('finance.paymentInstruments.types.bank_account', 'admin')}</SelectItem>
                                                <SelectItem value="credit_card">{t('finance.paymentInstruments.types.credit_card', 'admin')}</SelectItem>
                                                <SelectItem value="cash">{t('finance.paymentInstruments.types.cash', 'admin')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {errors.instrument_type && (
                                            <p className="text-sm text-red-600">{errors.instrument_type}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="account_number">{t('finance.paymentInstruments.fields.accountNumber', 'admin')}</Label>
                                        <Input
                                            id="account_number"
                                            value={formData.account_number}
                                            onChange={(e) => handleInputChange('account_number', e.target.value)}
                                            placeholder={t('finance.paymentInstruments.placeholders.accountNumber', 'admin')}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="iban">{t('finance.paymentInstruments.fields.iban', 'admin')}</Label>
                                        <Input
                                            id="iban"
                                            value={formData.iban}
                                            onChange={(e) => handleInputChange('iban', e.target.value)}
                                            placeholder={t('finance.paymentInstruments.placeholders.iban', 'admin')}
                                        />
                                    </div>


                                    <div className="space-y-2">
                                        <Label htmlFor="company_id">{t('finance.paymentInstruments.fields.company', 'admin')}</Label>
                                        <Select
                                            value={formData.company_id}
                                            onValueChange={(value) => handleInputChange('company_id', value)}
                                        >
                                            <SelectTrigger className={errors.company_id ? 'border-red-500' : ''}>
                                                <SelectValue placeholder={t('finance.paymentInstruments.placeholders.company', 'admin')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {companies.length === 0 ? (
                                                    <SelectItem value="loading" disabled>{t('finance.paymentInstruments.loadingCompanies', 'admin')}</SelectItem>
                                                ) : (
                                                    companies.map((c) => (
                                                        <SelectItem key={c.id} value={c.id}>
                                                            {c.name}
                                                        </SelectItem>
                                                    ))
                                                )}
                                            </SelectContent>
                                        </Select>
                                        {errors.company_id && (
                                            <p className="text-sm text-red-600">{errors.company_id}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="bank_name">{t('finance.paymentInstruments.fields.bankName', 'admin')}</Label>
                                        <Input
                                            id="bank_name"
                                            value={formData.bank_name}
                                            onChange={(e) => handleInputChange('bank_name', e.target.value)}
                                            placeholder={t('finance.paymentInstruments.placeholders.bankName', 'admin')}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="cost_center_id">{t('finance.paymentInstruments.fields.costCenterOptional', 'admin')}</Label>
                                        <Select
                                            value={formData.cost_center_id || 'all'}
                                            onValueChange={(value) => handleInputChange('cost_center_id', value === 'all' ? '' : value)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder={t('finance.paymentInstruments.allCenters', 'admin')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">{t('finance.paymentInstruments.allCenters', 'admin')}</SelectItem>
                                                {costCenters.map((costCenter) => (
                                                    <SelectItem key={costCenter.id} value={costCenter.id}>
                                                        {costCenter.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="is_active"
                                            checked={formData.is_active}
                                            onCheckedChange={(checked) => handleInputChange('is_active', checked as boolean)}
                                        />
                                        <Label htmlFor="is_active">{t('status.active', 'common')}</Label>
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => navigate({ to: '/admin/finance' })}
                                    >
                                        {t('actions.cancel', 'common')}
                                    </Button>
                                    <Button type="submit" disabled={saving}>
                                        {saving ? t('actions.saving', 'common') : t('actions.saveChanges', 'common')}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        </CurrentUserProvider>
    );
}

export const Route = createFileRoute('/admin/finance/payment-instruments/$id')({
    component: EditPaymentInstrumentPage,
})
